import { ListChecks } from 'lucide-react';
import EmptyState from '../../../shared/EmptyState';
import DataTable from '../../../shared/DataTable';
import TableSpinner from '../../../shared/TableSpinner';

const DocumentTypeFieldIcon = () => <ListChecks className="h-16 w-16" strokeWidth={1.5} />;

/** Get display label for document type: row may have documentType object or documentTypeId as string or populated object. */
function getDocumentTypeLabel(row) {
  const dt = row.documentType ?? (typeof row.documentTypeId === 'object' && row.documentTypeId ? row.documentTypeId : null);
  if (dt && typeof dt === 'object') return dt.name ?? dt.code ?? dt._id ?? '--';
  if (typeof row.documentTypeId === 'string') return row.documentTypeId;
  return '--';
}

const DocumentTypeFieldsTable = ({
  data,
  isLoading,
  isFetching,
  isError,
  error,
  onEdit,
  onDelete,
  isBusy = false,
  deletingId,
}) => {
  if (isLoading) {
    return <TableSpinner message="Loading document type fields…" />;
  }

  if (isError) {
    const message =
      error?.response?.data?.message || error?.message || 'Unable to load document type fields.';
    return (
      <EmptyState
        isError={true}
        title="Error loading document type fields"
        description={message}
        icon={<DocumentTypeFieldIcon />}
      />
    );
  }

  if (!data?.length) {
    return (
      <EmptyState
        title="No document type fields found"
        description="Adjust filters or create a document type field to see it listed here."
        icon={<DocumentTypeFieldIcon />}
      />
    );
  }

  const columns = [
    {
      key: 'name',
      header: 'Name',
      render: (row) => <span className="text-slate-600">{row.name}</span>,
    },
    {
      key: 'documentType',
      header: 'Document Type',
      render: (row) => (
        <span className="text-slate-600">
          {getDocumentTypeLabel(row)}
        </span>
      ),
    },
    {
      key: 'dataType',
      header: 'Data Type',
      render: (row) => <span className="text-slate-600">{row.dataType ?? '--'}</span>,
    },
    {
      key: 'required',
      header: 'Required',
      render: (row) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
            row.required ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
          }`}
        >
          {row.required ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'createdBy',
      header: 'Created By',
      render: (row) => {
        const by = row.createdBy;
        const label =
          by && typeof by === 'object'
            ? [by.firstName, by.lastName].filter(Boolean).join(' ') || '--'
            : '--';
        return <span className="text-slate-500">{label}</span>;
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (documentTypeField) => (
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onEdit?.(documentTypeField)}
            className="inline-flex items-center gap-1 rounded-lg border border-cream/60 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:bg-cream/60 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isBusy}
            aria-label={`Edit ${documentTypeField.name}`}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
            </svg>
            Edit
          </button>
          <button
            type="button"
            onClick={() => onDelete?.(documentTypeField)}
            className="inline-flex items-center gap-1 rounded-lg border border-red-100 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
            disabled={isBusy}
            aria-label={`Delete ${documentTypeField.name}`}
          >
            {isBusy && deletingId === documentTypeField._id ? (
              <span className="flex items-center gap-1">
                <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                Removing…
              </span>
            ) : (
              <>
                <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                </svg>
                Delete
              </>
            )}
          </button>
        </div>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data}
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      emptyTitle="No document type fields found"
      emptyMessage="Adjust filters or create a document type field to see it listed here."
      emptyIcon={<DocumentTypeFieldIcon />}
    />
  );
};

export default DocumentTypeFieldsTable;
